/* eslint-disable unicode-bom */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { apiUrl, fetchJson } from '../../apiBase';
import Skeleton from '@mui/material/Skeleton';

export default function LessonsManager({ schoolId, onError, onSuccess }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');

  const token = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('adminUser') || '{}')?.token; } catch (e) { return undefined; }
  }, []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchJson(apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      if (!r.ok) throw new Error('list');
      setLessons(await r.json());
    } catch (e) { onError && onError('Dersler yüklenemedi'); }
    finally { setLoading(false); }
  }, [onError, schoolId, authHeaders]);

  useEffect(() => { if (schoolId) load(); }, [schoolId, load]);

  const openNew = () => { setEditing(null); setName(''); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setName(l.name || ''); setOpen(true); };

  const save = async () => {
    try {
      if (!name.trim()) { onError && onError('Ders adı zorunlu'); return; }
      let r;
      if (editing) {
        r = await fetchJson(
          apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}/${encodeURIComponent(editing.name)}`),
          { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify({ name: name.trim() }) }
        );
      } else {
        r = await fetchJson(
          apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}`),
          { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify({ name: name.trim() }) }
        );
      }
      if (!r.ok) throw new Error('save');
      onSuccess && onSuccess('Ders kaydedildi'); setOpen(false); setEditing(null); load();
    } catch (e) { onError && onError('Ders kaydedilemedi'); }
  };

  const remove = async (l) => {
    if (!window.confirm('Ders silinsin mi?')) return;
    try {
      const r = await fetchJson(
        apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}/${encodeURIComponent(l.name)}`),
        { method: 'DELETE', headers: authHeaders }
      );
      if (!r.ok) throw new Error('del');
      onSuccess && onSuccess('Ders silindi'); load();
    } catch (e) { onError && onError('Ders silinemedi'); }
  };

  return (
    <>
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Dersler</Typography>
          <Button startIcon={<Add />} size="small" variant="contained" onClick={openNew}>Yeni Ders</Button>
        </Box>
        {loading ? (
          <List dense>
            {Array.from({ length: 6 }).map((_, i) => (
              <ListItem key={`s-${i}`}>
                <ListItemText primary={<Skeleton variant="text" width="40%" />} />
              </ListItem>
            ))}
          </List>
        ) : lessons.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>Henüz ders eklenmedi</Typography>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={openNew}>+ Yeni Ders</Button>
          </Box>
        ) : (
          <List dense>
            {lessons.map((l) => (
              <ListItem key={l.name}>
                <ListItemText primary={l.name} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => openEdit(l)}><Edit /></IconButton>
                  <IconButton edge="end" onClick={() => remove(l)}><Delete /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ders</DialogTitle>
        <DialogContent dividers>
          <TextField label="Ders Adı" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={save}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
